/**
 * Public `/install` page.
 *
 * The single canonical entry point for "wire SendFast into your AI tool".
 * Three layers from top to bottom:
 *
 *  1. Hero — pitch + the primary <InstallButtons /> row.
 *  2. Per-client cards — Cursor, VS Code, Claude Desktop, Claude.ai. Each
 *     card has its own Add button (so users can scroll to the host they
 *     actually use) plus copy-paste fallbacks.
 *  3. Generic snippet — the JSON config for any other MCP-aware host.
 *
 * Auto-fire flow: `/install?action=cursor` (or vscode / vscode-insiders)
 * fires the matching install once, post-mount, only when the user is
 * signed in. We use this for the post-signup redirect — clicking "Sign in
 * to add to Cursor" sends the visitor to /sign-up?return=/install&action=cursor,
 * and they land back here authenticated with the install already running.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, Check, Copy, ExternalLink, Loader2 } from 'lucide-react';
import { Wordmark } from '@/components/Wordmark';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useSession } from '@/hooks/useSession';
import { ApiError } from '@/lib/api';
import type { CreatedApiKey } from '@/lib/api-keys';
import {
  CLAUDE_DESKTOP_CONFIG_PATHS,
  MCP_ENDPOINT,
  buildCursorDeeplink,
  buildMcpConfigJson,
  buildVSCodeDeeplink,
  installClient,
  type InstallClient,
} from '@/lib/install';
import { ClaudeLogo, CursorLogo, VSCodeLogo } from '@/components/install/ClientLogos';

const AUTO_FIRE_CLIENTS = new Set<InstallClient>(['cursor', 'vscode', 'vscode-insiders']);

export function InstallPage() {
  const { user, loading } = useSession();
  const [params, setParams] = useSearchParams();
  const action = params.get('action');

  const [created, setCreated] = useState<CreatedApiKey | null>(null);
  const [busy, setBusy] = useState<InstallClient | null>(null);
  const [error, setError] = useState<string | null>(null);
  const autoFiredRef = useRef(false);

  useEffect(() => {
    document.title = 'Install — SendFast';
  }, []);

  // Single-fire auto-install from `?action=…`. We strip the param off the
  // URL once we've reacted to it so a refresh doesn't trigger a second
  // install (which would either reuse the cached key — fine but confusing
  // — or fail with the key-limit error if the cache is gone).
  useEffect(() => {
    if (autoFiredRef.current) return;
    if (loading) return;
    if (!action || !user) return;
    if (!isInstallClient(action) || !AUTO_FIRE_CLIENTS.has(action)) return;
    autoFiredRef.current = true;
    void runInstall(action);
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('action');
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot effect
  }, [loading, user]);

  async function runInstall(client: InstallClient) {
    if (!user) {
      window.location.assign(`/sign-up?return=/install&action=${encodeURIComponent(client)}`);
      return;
    }
    setError(null);
    setBusy(client);
    try {
      const result = await installClient(client, { autoMint: true }, user.id);
      if (result.created) setCreated(result.created);
    } catch (e) {
      if (e instanceof ApiError) setError(e.message);
      else if (e instanceof Error) setError(e.message);
      else setError('Install failed.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="bg-background text-foreground min-h-screen">
      <InstallHeader />

      <main className="mx-auto max-w-[1100px] px-6 pb-24 pt-12 lg:px-10">
        <header className="max-w-3xl">
          <Badge variant="secondary" className="rounded-full text-[10px] font-medium uppercase tracking-[0.18em]">
            One-click install
          </Badge>
          <h1 className="font-display mt-4 text-4xl font-semibold tracking-tight md:text-5xl">
            Add SendFast to your AI tool{' '}
            <span className="text-primary">in one click.</span>
          </h1>
          <p className="text-muted-foreground mt-5 text-lg leading-relaxed">
            Pick your host. We'll mint an API key, hand it to the client, and you'll see five
            email tools — <code className="text-foreground">draft_email</code>,{' '}
            <code className="text-foreground">modify_email</code>,{' '}
            <code className="text-foreground">screenshot_email</code>,{' '}
            <code className="text-foreground">send_email</code>,{' '}
            <code className="text-foreground">list_drafts</code> — appear in your tool picker.
          </p>
        </header>

        {!loading && !user ? (
          <Card className="border-primary/30 bg-primary/[0.04] mt-8 p-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="max-w-xl">
                <p className="text-sm font-medium">Sign in to use one-click install</p>
                <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
                  Each install link bakes a fresh API key into the deeplink so your client is
                  ready to use the moment it opens. Free accounts come with one key — sign up
                  takes 30 seconds.
                </p>
              </div>
              <div className="flex flex-shrink-0 gap-2">
                <Button variant="outline" asChild>
                  <Link to="/sign-in?return=/install">Sign in</Link>
                </Button>
                <Button asChild>
                  <Link to="/sign-up?return=/install">Create account</Link>
                </Button>
              </div>
            </div>
          </Card>
        ) : null}

        {error ? (
          <Card className="border-destructive/30 bg-destructive/[0.04] mt-8 p-4">
            <p className="text-destructive flex items-start gap-2 text-sm">
              <AlertCircle className="size-4 shrink-0 translate-y-[1px]" />
              <span className="leading-relaxed">{error}</span>
            </p>
          </Card>
        ) : null}

        {created ? <KeyRevealCard created={created} onDismiss={() => setCreated(null)} /> : null}

        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <CursorCard
            busy={busy === 'cursor'}
            onInstall={() => runInstall('cursor')}
            apiKey={created?.key ?? null}
          />
          <VSCodeCard
            busyVSCode={busy === 'vscode'}
            busyInsiders={busy === 'vscode-insiders'}
            onInstall={(v) => runInstall(v)}
            apiKey={created?.key ?? null}
          />
          <ClaudeDesktopCard apiKey={created?.key ?? null} />
          <ClaudeWebCard apiKey={created?.key ?? null} />
        </div>

        <GenericCard apiKey={created?.key ?? null} />
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function InstallHeader() {
  const { user } = useSession();
  return (
    <header className="border-border bg-background/80 sticky top-0 z-30 border-b backdrop-blur">
      <div className="mx-auto flex h-14 max-w-[1100px] items-center justify-between px-6 lg:px-10">
        <div className="flex items-center gap-3">
          <Wordmark to="/" />
          <span className="text-muted-foreground hidden text-sm sm:inline">/ Install</span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <Link
            to="/docs"
            className="text-muted-foreground hover:text-foreground transition"
          >
            Docs
          </Link>
          {user ? (
            <Button
              variant="secondary"
              size="sm"
              className="bg-foreground text-background hover:bg-foreground/90"
              asChild
            >
              <Link to="/app">Open dashboard →</Link>
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              className="bg-foreground text-background hover:bg-foreground/90"
              asChild
            >
              <Link to="/sign-up?return=/install">Sign up free</Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Cursor card
// ---------------------------------------------------------------------------

function CursorCard({
  busy,
  onInstall,
  apiKey,
}: {
  busy: boolean;
  onInstall: () => void;
  apiKey: string | null;
}) {
  const deeplink = buildCursorDeeplink(apiKey);
  return (
    <ClientCard
      logo={<CursorLogo className="size-6" />}
      name="Cursor"
      tagline="One-click install. Adds to your global MCP servers."
    >
      <Button onClick={onInstall} disabled={busy} className="w-full sm:w-auto">
        {busy ? <Loader2 className="size-4 animate-spin" /> : <CursorLogo className="size-4" />}
        {busy ? 'Opening Cursor…' : 'Add to Cursor'}
      </Button>
      <details className="group mt-2">
        <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-xs underline-offset-2 hover:underline">
          Manual install instead
        </summary>
        <div className="mt-3 space-y-2">
          <p className="text-muted-foreground text-xs leading-relaxed">
            Click or paste this link in your browser — Cursor will prompt to install:
          </p>
          <CopyableCode code={deeplink} small />
          <p className="text-muted-foreground text-xs leading-relaxed">
            Or paste the JSON below into{' '}
            <code className="text-foreground">~/.cursor/mcp.json</code>:
          </p>
          <CopyableCode code={buildMcpConfigJson(apiKey)} />
        </div>
      </details>
    </ClientCard>
  );
}

// ---------------------------------------------------------------------------
// VS Code card
// ---------------------------------------------------------------------------

function VSCodeCard({
  busyVSCode,
  busyInsiders,
  onInstall,
  apiKey,
}: {
  busyVSCode: boolean;
  busyInsiders: boolean;
  onInstall: (variant: 'vscode' | 'vscode-insiders') => void;
  apiKey: string | null;
}) {
  return (
    <ClientCard
      logo={<VSCodeLogo className="size-6" />}
      name="VS Code"
      tagline="For GitHub Copilot Chat with MCP support."
    >
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => onInstall('vscode')}
          disabled={busyVSCode}
          className="flex-1 sm:flex-initial"
        >
          {busyVSCode ? <Loader2 className="size-4 animate-spin" /> : <VSCodeLogo className="size-4" />}
          {busyVSCode ? 'Opening…' : 'Add to VS Code'}
        </Button>
        <Button
          variant="outline"
          onClick={() => onInstall('vscode-insiders')}
          disabled={busyInsiders}
          className="flex-1 sm:flex-initial"
        >
          {busyInsiders ? <Loader2 className="size-4 animate-spin" /> : <VSCodeLogo className="size-4" />}
          {busyInsiders ? 'Opening…' : 'Insiders'}
        </Button>
      </div>
      <details className="group mt-2">
        <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-xs underline-offset-2 hover:underline">
          Manual install instead
        </summary>
        <div className="mt-3 space-y-2">
          <p className="text-muted-foreground text-xs leading-relaxed">
            Click the deeplink, or run from the terminal:
          </p>
          <CopyableCode
            code={`code --add-mcp '${JSON.stringify({
              name: 'sendfast',
              type: 'http',
              url: MCP_ENDPOINT,
              headers: { Authorization: `Bearer ${apiKey ?? 'sf_live_REPLACE_ME'}` },
            })}'`}
          />
          <p className="text-muted-foreground text-xs leading-relaxed">
            Deeplink (also works from a browser):
          </p>
          <CopyableCode code={buildVSCodeDeeplink(apiKey, 'vscode')} small />
        </div>
      </details>
    </ClientCard>
  );
}

// ---------------------------------------------------------------------------
// Claude Desktop card
// ---------------------------------------------------------------------------

function ClaudeDesktopCard({ apiKey }: { apiKey: string | null }) {
  const snippet = buildMcpConfigJson(apiKey);
  return (
    <ClientCard
      logo={<ClaudeLogo className="size-6" />}
      name="Claude Desktop"
      tagline="No deeplink yet — paste the snippet, restart Claude."
    >
      <ol className="text-muted-foreground space-y-1.5 text-sm leading-relaxed">
        <li>
          <span className="text-foreground font-medium">1.</span> Open the config file for your
          OS:
        </li>
      </ol>
      <ul className="text-muted-foreground mt-1 space-y-1 text-xs">
        <li className="flex flex-wrap items-baseline gap-2">
          <span className="text-foreground/70 w-16 shrink-0 font-medium">macOS</span>
          <code className="font-mono">{CLAUDE_DESKTOP_CONFIG_PATHS.macos}</code>
        </li>
        <li className="flex flex-wrap items-baseline gap-2">
          <span className="text-foreground/70 w-16 shrink-0 font-medium">Windows</span>
          <code className="font-mono">{CLAUDE_DESKTOP_CONFIG_PATHS.windows}</code>
        </li>
        <li className="flex flex-wrap items-baseline gap-2">
          <span className="text-foreground/70 w-16 shrink-0 font-medium">Linux</span>
          <code className="font-mono">{CLAUDE_DESKTOP_CONFIG_PATHS.linux}</code>
        </li>
      </ul>
      <p className="text-muted-foreground mt-3 text-sm">
        <span className="text-foreground font-medium">2.</span> Paste this and save:
      </p>
      <CopyableCode code={snippet} />
      <p className="text-muted-foreground text-sm leading-relaxed">
        <span className="text-foreground font-medium">3.</span> Quit and reopen Claude. SendFast
        appears in the tool picker.
      </p>
    </ClientCard>
  );
}

// ---------------------------------------------------------------------------
// Claude.ai (web) card
// ---------------------------------------------------------------------------

function ClaudeWebCard({ apiKey }: { apiKey: string | null }) {
  return (
    <ClientCard
      logo={<ClaudeLogo className="size-6" />}
      name="Claude.ai (web)"
      tagline="Add as a Custom Connector under Settings → Connectors."
    >
      <ol className="text-muted-foreground space-y-2 text-sm leading-relaxed">
        <li>
          <span className="text-foreground font-medium">1.</span> Open{' '}
          <a
            href="https://claude.ai/settings/connectors"
            target="_blank"
            rel="noreferrer"
            className="text-primary inline-flex items-center gap-0.5 hover:underline"
          >
            Settings → Connectors <ExternalLink className="size-3" />
          </a>{' '}
          and click <strong className="text-foreground">Add custom connector</strong>.
        </li>
        <li>
          <span className="text-foreground font-medium">2.</span> Use these values:
        </li>
      </ol>
      <dl className="text-muted-foreground border-border bg-muted/40 mt-3 grid grid-cols-[80px_1fr] gap-x-4 gap-y-2 rounded-md border p-3 text-xs">
        <dt className="text-foreground/70">Name</dt>
        <dd>
          <code className="font-mono">SendFast</code>
        </dd>
        <dt className="text-foreground/70">URL</dt>
        <dd>
          <CopyableInline value={MCP_ENDPOINT} />
        </dd>
        <dt className="text-foreground/70">Auth</dt>
        <dd>
          Custom header → <code className="font-mono">Authorization</code> →{' '}
          <CopyableInline value={`Bearer ${apiKey ?? 'sf_live_REPLACE_ME'}`} />
        </dd>
      </dl>
      <p className="text-muted-foreground text-sm leading-relaxed">
        <span className="text-foreground font-medium">3.</span> Save. SendFast tools show up in
        any new chat.
      </p>
    </ClientCard>
  );
}

// ---------------------------------------------------------------------------
// Generic / any other client
// ---------------------------------------------------------------------------

function GenericCard({ apiKey }: { apiKey: string | null }) {
  return (
    <Card className="border-border bg-muted/40 mt-10 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <h2 className="text-base font-semibold tracking-tight">Any other MCP client</h2>
          <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
            LangGraph, CrewAI, Zed, custom Node / Python — drop the snippet anywhere that
            speaks the streamable-HTTP MCP transport.
          </p>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to="/docs#mcp">
            Read MCP docs <ExternalLink className="size-3.5" />
          </Link>
        </Button>
      </div>
      <div className="mt-5">
        <CopyableCode code={buildMcpConfigJson(apiKey)} />
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Reveal card — shown after a successful auto-mint
// ---------------------------------------------------------------------------

function KeyRevealCard({
  created,
  onDismiss,
}: {
  created: CreatedApiKey;
  onDismiss: () => void;
}) {
  return (
    <Card className="border-primary/30 bg-primary/[0.04] mt-8 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">Your install key is ready.</p>
          <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
            We minted{' '}
            <code className="text-foreground">{created.name ?? 'an API key'}</code> and embedded
            it in every install button below — clicking another host (e.g. Cursor + VS Code)
            reuses the same key, no extra quota burned. Save it now if you want a copy: we
            won't show the plaintext again.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
      <div className="bg-background border-border mt-3 flex items-center gap-2 rounded-md border p-3">
        <code className="min-w-0 flex-1 truncate font-mono text-xs">{created.key}</code>
        <CopyableInlineButton value={created.key} />
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

function ClientCard({
  logo,
  name,
  tagline,
  children,
}: {
  logo: React.ReactNode;
  name: string;
  tagline: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="flex flex-col gap-4 p-6">
      <div className="flex items-center gap-3">
        <div className="bg-foreground/[0.04] text-foreground flex size-10 shrink-0 items-center justify-center rounded-lg">
          {logo}
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-semibold tracking-tight">{name}</h3>
          <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">{tagline}</p>
        </div>
      </div>
      {children}
    </Card>
  );
}

function CopyableCode({ code, small = false }: { code: string; small?: boolean }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }
  return (
    <div className="bg-foreground text-background border-foreground/10 group relative overflow-hidden rounded-md border">
      <button
        type="button"
        onClick={copy}
        className="text-background/80 hover:bg-white/10 hover:text-background absolute right-1.5 top-1.5 inline-flex items-center gap-1 rounded px-1.5 py-1 text-[10px] font-medium uppercase tracking-wider opacity-0 transition group-hover:opacity-100 focus-visible:opacity-100"
        aria-label="Copy"
      >
        {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
        {copied ? 'copied' : 'copy'}
      </button>
      <pre
        className={
          'overflow-x-auto px-3 py-2.5 font-mono leading-snug ' + (small ? 'text-[10px]' : 'text-[11px]')
        }
      >
        <code className="break-all">{code}</code>
      </pre>
    </div>
  );
}

function CopyableInline({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }
  return (
    <button
      type="button"
      onClick={copy}
      title="Click to copy"
      className="text-foreground hover:text-primary inline-flex items-center gap-1.5 break-all text-left font-mono"
    >
      <span>{value}</span>
      <span className="text-muted-foreground text-[9px] uppercase tracking-wider">
        {copied ? '✓' : 'copy'}
      </span>
    </button>
  );
}

function CopyableInlineButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }
  return (
    <Button variant="outline" size="sm" onClick={copy}>
      {copied ? (
        <>
          <Check className="size-3" /> Copied
        </>
      ) : (
        <>
          <Copy className="size-3" /> Copy
        </>
      )}
    </Button>
  );
}

function isInstallClient(value: string): value is InstallClient {
  return (
    value === 'cursor' ||
    value === 'vscode' ||
    value === 'vscode-insiders' ||
    value === 'claude-desktop' ||
    value === 'claude-web'
  );
}
